import React, { useState, useEffect, useMemo } from "react";
import "./AdminWorkers.css";
import bfLogo from "../assets/bf_logo.png";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Info, Pencil, Trash2, Search, ArrowUpDown, ArrowLeft, Archive } from "lucide-react";

import WorkerInfoModal from "../components/WorkerInfoModal";
import AddWorkerModal from "../components/AddWorkerModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

/* ===== FIREBASE ===== */
import { auth, db } from "../firebase";
import {
  collection, onSnapshot, query, where, addDoc, updateDoc, doc, getDoc, serverTimestamp
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

// --- PAKEISTA: Limitai dabar atitinka naują kainodarą ---
const PLAN_LIMITS = {
  active_basis: 5,      // Pakeista iš 10 į 5
  active_growth: 15,    // Pakeista iš 30 į 15
  active_pro: 35,       // Pakeista iš 50 į 35
  enterprise: 999
};

function AdminWorkers() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [workers, setWorkers] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const storage = getStorage();
  const isAnyModalOpen = showInfo || showEdit || showDelete;

  useEffect(() => {
    let unsubscribeSnapshot = () => {};
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const uData = userSnap.data();
          setUserData(uData);
          const masterId = uData.belongsTo || user.uid;
          
          const q = query(collection(db, "workers"), where("adminId", "==", masterId));
          unsubscribeSnapshot = onSnapshot(q, (snap) => {
            const allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            const activeWorkers = allDocs.filter(w => !w.archived);
            setWorkers(activeWorkers);
            setLoading(false);
          }, (error) => {
            console.error("Snapshot error:", error);
            setLoading(false);
          });
        }
      } else { navigate("/admin/login"); }
    });
    return () => { unsubscribeAuth(); unsubscribeSnapshot(); };
  }, [navigate]);

  const handleArchiveWorker = async () => {
    if (!selectedWorker) return;
    try {
        await updateDoc(doc(db, "workers", selectedWorker.id), {
            archived: true,
            archivedAt: new Date().toISOString()
        });
        setShowDelete(false);
    } catch (e) {
        alert("Klaida archyvuojant: " + e.message);
    }
  };

  const handleSaveWorker = async (id, data) => {
    if (!data.name || data.name.trim() === "") {
        alert(t('error_name_required'));
        return;
    }
    const user = auth.currentUser;
    if (!user || !userData) return;
    const masterId = userData.belongsTo || user.uid;

    const isDuplicate = workers.some(w => {
      if (id && w.id === id) return false;
      return w.name.trim().toLowerCase() === data.name.trim().toLowerCase() || 
             (data.email && w.email?.trim().toLowerCase() === data.email.trim().toLowerCase());
    });

    if (isDuplicate) { alert(t('duplicate_worker_error')); return; }

    try {
      setLoading(true);
      let finalDocuments = [...(data.documents || [])];
      if (data.newFiles?.length > 0) {
        for (const fileItem of data.newFiles) {
          const storageRef = ref(storage, `workers_docs/${masterId}/${Date.now()}_${fileItem.file.name}`);
          await uploadBytes(storageRef, fileItem.file);
          const downloadURL = await getDownloadURL(storageRef);
          finalDocuments.push({
            name: fileItem.customName || fileItem.file.name,
            url: downloadURL,
            uploadedAt: new Date().toISOString()
          });
        }
      }

      const workerPayload = {
        name: data.name, email: data.email, phone: data.phone,
        customFields: data.customFields || [], documents: finalDocuments,
        adminId: masterId, createdAt: data.createdAt ? new Date(data.createdAt) : serverTimestamp(),
        archived: false, 
        ...(id ? {} : { loginCode: Math.floor(100000 + Math.random() * 900000).toString(), codeUsed: false })
      };

      if (id) await updateDoc(doc(db, "workers", id), workerPayload);
      else {
        // --- PAKEISTA: Naudojami nauji limitai ---
        const maxLimit = userData?.workerLimit || PLAN_LIMITS[userData?.subscriptionStatus?.toLowerCase()] || 5;
        if (workers.length >= maxLimit) {
          alert(`${t('limit_reached')} (${maxLimit})`);
          setLoading(false); return;
        }
        await addDoc(collection(db, "workers"), workerPayload);
      }
      setShowEdit(false); setLoading(false);
    } catch (e) { alert(t('error_saving') + e.message); setLoading(false); }
  };

  const filteredWorkers = useMemo(() => {
    return workers
      .filter(w => w.name?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name));
  }, [workers, sortAsc, search]);

  if (loading) return <div className="loading-scr">{t('loading')}</div>;

  return (
    <div className="admin-workers-root">
      <button className="gold-back-btn" onClick={() => navigate('/admin')}>
          <ArrowLeft size={18} style={{ marginRight: '8px' }} /> {t('back')}
      </button>

      <div 
        className="main-content-wrapper" 
        style={{ 
          pointerEvents: isAnyModalOpen ? 'none' : 'auto', 
          filter: isAnyModalOpen ? 'blur(2px)' : 'none',
          display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden'
        }}
      >
        <div className="main-wrapper-1280">
          <img src={bfLogo} alt="logo" className="logo-top" />
          <h1 className="title-top">{t('workers')}</h1>

          <div className="top-pills-row">
            <div className="pill search-pill">
              <Search size={18} color="#888" />
              <input placeholder={t('search')} value={search} onChange={e => setSearch(e.target.value)} disabled={isAnyModalOpen} />
            </div>
            
            <div className="pill count-pill">
              {/* PAKEISTA: Default skaičius į 5 */}
              {workers.length} / {userData?.workerLimit || 5} {t('employees')}
            </div>

            <button 
                className="pill archive-pill" 
                onClick={() => navigate('/admin/workers/archive')}
            >
                <Archive size={16} style={{marginRight: '8px'}}/> {t('archive') || "Archyvas"}
            </button>

            <button className="pill add-pill" onClick={() => { setSelectedWorker(null); setShowEdit(true); }}>
              + {t('worker')}
            </button>
          </div>

          <div className="worker-header-line">
            <div className="c-id">#</div>
            <div className="c-name-header clickable" onClick={() => setSortAsc(!sortAsc)}>
              {t('name')} <ArrowUpDown size={14} style={{marginLeft: '5px'}} />
            </div>
            <div className="c-mail">{t('email')}</div>
            <div className="c-phone">{t('phone')}</div>
            <div className="c-acts">{t('actions')}</div>
          </div>

          <div className="worker-rows-scroll-list">
            {filteredWorkers.map((w, i) => (
              <div key={w.id} className="worker-item-row">
                <div className="c-id val-white">{i + 1}</div>
                <div className="c-name val-white">{w.name}</div>
                <div className="c-mail val-gray">{w.email}</div>
                <div className="c-phone val-gray">{w.phone || "-"}</div>
                <div className="c-acts">
                  <div className="action-row-flex">
                    <button className="btn-act info" onClick={() => { setSelectedWorker(w); setShowInfo(true); }}><Info size={18} /></button>
                    <button className="btn-act edit" onClick={() => { setSelectedWorker(w); setShowEdit(true); }}><Pencil size={18} /></button>
                    <button 
                        className="btn-act del" 
                        onClick={() => { setSelectedWorker(w); setShowDelete(true); }}
                        title={t('archive_worker_confirm') || "Archyvuoti"}
                    >
                        <Archive size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showInfo && <WorkerInfoModal worker={selectedWorker} onClose={() => setShowInfo(false)} />}
      
      {showEdit && (
        <AddWorkerModal 
          initialData={selectedWorker} 
          companyInfo={{ name: userData?.companyName, address: userData?.address }}
          currentUser={userData?.name}
          onClose={() => setShowEdit(false)} 
          onSave={handleSaveWorker} 
        />
      )}

      {showDelete && (
        <DeleteConfirmModal 
            worker={selectedWorker} 
            onClose={() => setShowDelete(false)} 
            onConfirm={handleArchiveWorker} 
            title={t('archive_worker_confirm')}
            message={t('archive_worker_desc')}
            
            isArchive={true} 
            btnText={t('archive') || "Archyvuoti"} 
        />
      )}
    </div>
  );
}

export default AdminWorkers;